<template>
  <div>
    <Modal
      v-if="availabiltyModal"
      title="Check Availability"
      submit-btn-text="Schedule"
      @hide-modal="hideModals"
      @submit="handleSave"
    >
      <div class="flex flex-col gap-3">
        <InputField
          v-model="formData.zipCode"
          title="Zip Code"
          :validation="firstErrorMessage(v$.zipCode) ? 'error' : ''"
          :helper-text="firstErrorMessage(v$.zipCode)"
        />
        <div class="flex flex-col md:flex-row gap-3 w-full">
          <SelectInput
            v-model="formData.service_category_id"
            label="Service Category"
            :options="serviceCategoryOptions"
            :filter="true"
            :body-render="false"
            filter-placeholder="Search categories..."
            options-class="!max-h-[165px]"
            :validation="
              firstErrorMessage(v$.service_category_id) ? 'error' : ''
            "
            :helper-text="firstErrorMessage(v$.service_category_id)"
          />
          <div class="w-full relative">
            <Checkbox
              v-model="allTechs"
              label="Include all technicians"
              class="absolute right-0"
              @update:modelValue="(e) => (allTechs = e)"
            ></Checkbox>
            <SelectInput
              v-model="formData.technician_id"
              label="Technician"
              :options="technicianOptions"
              :body-render="false"
              :filter="true"
              filter-placeholder="Search technicians..."
              placeholder="Auto Assign"
              options-class="!max-h-[165px]"
            />
          </div>
        </div>
        <div v-if="formData.zipCode && selectedServiceCategory">
          <AppointmentAvailabilityInput
            :service-category-id="formData.service_category_id"
            :duration-minutes="selectedServiceCategory?.duration"
            :zipcode="formData.zipCode"
            :user-id="selectedTech?.user_id"
            :validation="firstErrorMessage(v$.scheduled_time)"
            @update:modelValue="handleTimeSelected"
          />
        </div>
      </div>
    </Modal>
    <AppointmentCreateModal
      v-if="createModal"
      :scheduled_time="formData.scheduled_time"
      :arrival_window_start="formData.arrival_window_start"
      :arrival_window_end="formData.arrival_window_end"
      :service_category_id="formData.service_category_id"
      :technician_id="formData.technician_id"
      :unavailable_time="formData.unavailable_time"
      @hide-modal="hideModals"
      @created="refreshTimelineEvents"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Modal from "@/components/Modal.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import AppointmentAvailabilityInput from "@/AppointmentCreateModal/AppointmentAvailabilityInput.vue";
import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import { useAppointmentSchedulingFields } from "@/AppointmentCreateModal/useAppointmentSchedulingFields";
import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";

const emit = defineEmits(["hide-modal", "created"]);
const { refreshTimeline } = useEventCalendar();
const formData = ref<{
  scheduled_time: string;
  service_category_id: number | null;
  technician_id: number | null;
  zipCode: string;
  arrival_window_start: string;
  arrival_window_end: string;
  unavailable_time: boolean;
}>({
  scheduled_time: "",
  service_category_id: null,
  technician_id: null,
  zipCode: "",
  arrival_window_start: "",
  arrival_window_end: "",
  unavailable_time: false
});
const store = useStore();
const saving = ref(false);
const createModal = ref(false);
const availabiltyModal = ref(true);
const allTechs = ref(false);

const {
  serviceCategoryOptions,
  technicianOptions,
  selectedServiceCategory,
  selectedTech
} = useAppointmentSchedulingFields(formData, allTechs);

const validateCoverageArea = async (zipcode: string) => {
  if (!zipcode || zipcode.length < 5) return true;
  try {
    await store.dispatch("validateCoverageArea", {
      email: "",
      zip_code: zipcode
    });
    return true;
  } catch {
    return false;
  }
};

const v$ = useVuelidate(
  {
    zipCode: {
      required: helpers.withMessage("Zip Code is required", required),
      coverageArea: helpers.withMessage(
        "Zip code is not in your coverage area!",
        helpers.withAsync(validateCoverageArea)
      ),
      $autoDirty: true
    },
    scheduled_time: {
      required: helpers.withMessage("Scheduled time is required", required)
    },
    service_category_id: {
      required: helpers.withMessage("Service category is required", required)
    }
  },
  formData.value
);

const handleSave = async () => {
  saving.value = true;
  try {
    const valid = await v$.value.$validate();
    if (valid) {
      toggleAvailabilityModal();
      toggleCreateModal();
    }
  } catch (e) {
    console.error(e);
  }
  saving.value = false;
};

const handleTimeSelected = (data: {
  scheduled_time: string;
  arrival_window_start: string;
  arrival_window_end: string;
  unavailable_time: boolean;
}) => {
  formData.value.scheduled_time = data.scheduled_time;
  formData.value.arrival_window_start = data.arrival_window_start;
  formData.value.arrival_window_end = data.arrival_window_end;
  formData.value.unavailable_time = data.unavailable_time;
};

const toggleAvailabilityModal = () => {
  availabiltyModal.value = !availabiltyModal.value;
};
const toggleCreateModal = () => {
  createModal.value = !createModal.value;
};
const hideModals = () => {
  availabiltyModal.value = false;
  createModal.value = false;
  emit("hide-modal");
};
const refreshTimelineEvents = () => {
  refreshTimeline();
};
</script>

<style scoped></style>
